import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useHistory, useLocation } from 'react-router-dom'
import dayjs from 'dayjs'
import { Button } from '@/components/atoms/button'
import { wholeContainerStyle, mainColumnStyle, mainContainerFooterBarStyle } from '@/constants/layout'
import { AccountContext } from '@/contexts/account'
import { useSalesEditGuard } from '@/hooks/use-sales-edit-guard'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { LoadingFull } from '@/components/molecules/loading-full'
import { Transaction } from '@/components/organisms/sales-manager/transaction'
import * as api from '@/apis/aipass'
import {
  PluginCode,
  checkActivePlugin,
  setHasAccountingInterlockingPlugin,
  setHasNotPaymentSalePlugin,
  setHasReservationAnalysisPlugin,
} from '@/libs/plugins'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { DailySummary } from '@/models/sales-manager/detail'
import { BalanceSheet } from '@/components/organisms/sales-manager/closing-detail/balance-sheet'
import { Cash } from '@/components/organisms/sales-manager/closing-detail/cash'
import { Report } from '@/components/organisms/sales-manager/closing-detail/report'
import { Payment } from '@/components/organisms/sales-manager/closing-detail/payment'
import { Sales } from '@/components/organisms/sales-manager/closing-detail/sales'
import { DetailHeader } from '@/components/organisms/sales-manager/closing-detail/header'
import { ConfirmModal } from '@/components/organisms/sales-manager/closing-detail/confirm-modal'
import { SalesConfirmErrorModal } from '@/components/organisms/sales-manager/closing-detail/sales-confirm-error-modal'
import { isSalesConfirmErrorResponse } from '@/models/sales-manager/sales-confirm-error'
import type { SalesConfirmErrorResponse } from '@/models/sales-manager/sales-confirm-error'
import { downloadInline } from '@/libs/csv-download'

export const SalesManagerDetailByDaily: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { id: salesManagerDailyId, transactionId } = require('query-string').parse(window.location.search)
  const { plugins } = useContext<any>(AccountContext)
  const { isSalesReadOnly } = useSalesEditGuard()
  const mode = 'daily'

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [salesTotalDetail, setSalesTotalDetail] = useState<DailySummary>()

  const [confirmModalVisible, setConfirmModalVisible] = useState<boolean>(false)
  const [confirmError, setConfirmError] = useState<SalesConfirmErrorResponse | null>(null)
  const history = useHistory()
  const location = useLocation()

  // Original sales data (for comparison when updating)
  const hasAccountingInterlockingPlugin = setHasAccountingInterlockingPlugin(plugins)
  const hasReservationAnalysisPlugin = setHasReservationAnalysisPlugin(plugins)
  const hasNotPaymentSalePlugin = setHasNotPaymentSalePlugin(plugins)
  const isActivePluginSmaregi = checkActivePlugin(plugins, PluginCode.Smaregi)

  const onClickConfirm = async () => {
    if (isSalesReadOnly) {
      alert(t('HotelPms.Error.readOnly'))
      return
    }
    if (hasNotPaymentSalePlugin) {
      if (salesTotalDetail?.indicator.unpaidTotal) {
        alert(t('There are some courses that have not been settled so please mark them as settled before confirming'))
        return
      }
    }
    setConfirmModalVisible(true)
  }

  const onClickModalConfirm = async (date, startDatePeriod) => {
    if (isSalesReadOnly) {
      alert(t('HotelPms.Error.readOnly'))
      return
    }
    try {
      setIsLoading(true)
      setConfirmModalVisible(false)
      const { nextSalesManagerId } = await api.confirmSalesDetail({ salesManagerDailyId })

      // CSV 権限がない場合は自動連動フロー内で alert せずスキップする（締め確定後の混乱を防ぐ）
      if (hasAccountingInterlockingPlugin) {
        const endDatePeriod = dayjs().format('YYYY-MM-DD H:mm:ss')
        const [rev, cl] = await Promise.all([
          api.fetchComExportRevCsv(date, startDatePeriod, endDatePeriod),
          api.fetchComExportCLCsv(date, startDatePeriod, endDatePeriod),
        ])
        downloadInline(rev.url)
        await new Promise(resolve => setTimeout(resolve, 500))
        downloadInline(cl.url)
      }
      if (hasReservationAnalysisPlugin) {
        const exportDate = dayjs(date).format('YYYYMMDD')
        try {
          await api.exportBookingData(exportDate)
        } catch (err) {
          alert(t('Failed to send file Please contact the site administrator'))
        }
        try {
          await api.exportAccommodationData(exportDate)
        } catch (err) {
          alert(t('Failed to send file Please contact the site administrator'))
        }
      }
      history.push({
        search: new URLSearchParams({ id: nextSalesManagerId }).toString(),
      })
    } catch (error: unknown) {
      const data = (error as { response?: { data?: unknown } })?.response?.data
      if (isSalesConfirmErrorResponse(data)) {
        setConfirmError(data)
        // ブラウザバックで戻ってきたときにモーダルを再表示するため、履歴 state に載せる
        history.replace({ pathname: location.pathname, search: location.search, state: { salesConfirmError: data } })
      } else {
        errorHandler(error)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onClickModalCancel = () => {
    setConfirmModalVisible(false)
  }

  const onCloseConfirmErrorModal = () => {
    setConfirmError(null)
  }

  const isDetailToConfirmation = useMemo(() => {
    if (!salesTotalDetail || salesTotalDetail.isConfirm) {
      return false
    }
    return dayjs(salesTotalDetail.payments.summaryTo || 0).isSame(dayjs.unix(2147483647), 'day')
  }, [salesTotalDetail])

  useEffect(() => {
    if (!salesManagerDailyId) {
      return
    }
    setIsLoading(true)
    api.fetchSalesManagerDailySummary({ salesManagerDailyId }).then(res => {
      if (!res) {
        alert('締め状況が登録されていません')
        history.goBack()
        return
      }
      setSalesTotalDetail(res)
      setIsLoading(false)
    })
  }, [salesManagerDailyId])

  // ブラウザバックで戻ってきたときに、履歴 state から確定エラーを復元してモーダルを再表示する
  useEffect(() => {
    const restored = (location.state as { salesConfirmError?: SalesConfirmErrorResponse })?.salesConfirmError
    if (restored && isSalesConfirmErrorResponse(restored)) {
      setConfirmError(restored)
      history.replace({ pathname: location.pathname, search: location.search, state: {} })
    }
  }, [])

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div className="mainColumn" css={mainColumnStyle}>
        <Header isBack title={t('Sales management')} goBack={history.goBack} />
        {salesTotalDetail && (
          <>
            <div css={mainWrapperStyle}>
              <DetailHeader salesTotalDetail={salesTotalDetail} mode={mode} />
              <div css={cardFlexStyle}>
                <div>
                  <Sales salesTotalDetail={salesTotalDetail} mode={mode} />
                  <BalanceSheet salesTotalDetail={salesTotalDetail} />
                  <Report salesManagerDailyId={salesManagerDailyId} isConfirm={salesTotalDetail.isConfirm} />
                </div>
                <div>
                  <Payment salesTotalDetail={salesTotalDetail} />
                  <Cash salesManagerDailyId={salesManagerDailyId} isConfirm={salesTotalDetail.isConfirm} />
                </div>
              </div>
              {isActivePluginSmaregi && <Transaction salesDate={salesTotalDetail.sales.summaryFrom} transactionId={transactionId} />}
            </div>
            <div css={mainContainerFooterBarStyle}>
              {isDetailToConfirmation && !isSalesReadOnly && (
                <Button width={110} height={38} buttonType={1} onClick={onClickConfirm}>
                  {t('Confirm')}
                </Button>
              )}
            </div>
            {confirmModalVisible && (
              <ConfirmModal
                salesTotalDetail={salesTotalDetail}
                onClickModalConfirm={onClickModalConfirm}
                onClickModalCancel={onClickModalCancel}
              />
            )}
            {confirmError && <SalesConfirmErrorModal error={confirmError} onClose={onCloseConfirmErrorModal} />}
          </>
        )}
        <LoadingFull isLoading={isLoading} />
      </div>
    </div>
  )
}

const cardFlexStyle = css({
  display: 'flex',
  gap: 16,
  '> div': { width: '50%', gap: 16, display: 'flex', flexDirection: 'column' },
})

const mainWrapperStyle = css({
  width: '100%',
  padding: '24px 24px 0',
  marginBottom: 70,
  overflow: 'scroll',
  '::-webkit-scrollbar': {
    display: 'none',
  },
})
